import { useCallback, useState, ComponentProps, useMemo } from 'react';
import { Snackbar, tailwindOverride } from '@getsynapse/design-system';

export type SnackbarProps = ComponentProps<typeof Snackbar> & {
  id: string;
};

interface NotificationsMap {
  [id: string]: SnackbarProps;
}

const useSnackbarNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationsMap>({});

  const hideNotification = useCallback((id: string) => {
    setNotifications((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications({});
  }, []);

  const showNotification = useCallback(
    (notificationProps: SnackbarProps) => {
      setNotifications((prev) => ({
        ...prev,
        [notificationProps.id]: {
          ...notificationProps,
          onCloseCallback: () => {
            notificationProps.onCloseCallback &&
              notificationProps.onCloseCallback();
            hideNotification(notificationProps.id);
          },
          className: tailwindOverride(
            'relative bg-neutral-white',
            'animate-snackarNotification',
            notificationProps.className
          ),
          autoHideDuration: notificationProps.autoHideDuration ?? 4000,
        },
      }));
    },
    [hideNotification]
  );

  const notificationsIds = useMemo(() => {
    return Object.keys(notifications);
  }, [notifications]);

  return useMemo(
    () => ({
      notifications: Object.values(notifications),
      showNotification,
      hideNotification,
      clearNotifications,
      notificationsIds,
    }),
    [
      notifications,
      showNotification,
      hideNotification,
      clearNotifications,
      notificationsIds,
    ]
  );
};

export default useSnackbarNotifications;
